import { Lightbulb } from 'lucide-react';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

interface Suggestion {
  id: string;
  text: string;
  emoji?: string;
  category: string;
  createdAt: string;
}

interface SuggestionsListProps {
  suggestions: Suggestion[];
  onSuggestionClick: (text: string) => void;
  isLoading?: boolean;
}

// In-memory cache for emoji colors (persists across re-renders)
const emojiColorCache = new Map<string, string>();

// Extract dominant color from emoji using canvas
function getEmojiDominantColor(emoji: string): string {
  // Check cache first
  if (emojiColorCache.has(emoji)) {
    return emojiColorCache.get(emoji)!;
  }
  
  // Calculate color
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return '#64748B'; // Fallback gray
    
    canvas.width = canvas.height = 64;
    
    // Draw emoji
    ctx.font = '48px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(emoji, 32, 32);
    
    // Get pixel data
    const imageData = ctx.getImageData(0, 0, 64, 64);
    const pixels = imageData.data;
    
    // Count colors (skip transparent and near-black pixels)
    const colorCounts: Record<string, number> = {};
    for (let i = 0; i < pixels.length; i += 4) {
      const a = pixels[i + 3];
      const r = pixels[i];
      const g = pixels[i + 1];
      const b = pixels[i + 2];
      
      // Skip transparent and very dark pixels (likely shadows)
      if (a > 128 && (r + g + b) > 100) {
        const color = `${r},${g},${b}`;
        colorCounts[color] = (colorCounts[color] || 0) + 1;
      }
    }
    
    // Find most common color
    const entries = Object.entries(colorCounts);
    if (entries.length === 0) {
      const fallback = '#64748B';
      emojiColorCache.set(emoji, fallback); // Cache fallback
      return fallback;
    }
    
    const dominant = entries.sort((a, b) => b[1] - a[1])[0];
    const [r, g, b] = dominant[0].split(',').map(Number);
    const color = `rgb(${r}, ${g}, ${b})`;
    
    // Cache the result
    emojiColorCache.set(emoji, color);
    return color;
  } catch (error) {
    console.error('Error extracting emoji color:', error);
    const fallback = '#64748B';
    emojiColorCache.set(emoji, fallback); // Cache fallback
    return fallback;
  }
}

export default function SuggestionsList({ 
  suggestions, 
  onSuggestionClick,
  isLoading = false 
}: SuggestionsListProps) {
  const [emojiColors, setEmojiColors] = useState<Record<string, string>>({});
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Detect dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };
    
    checkDarkMode();
    
    // Watch for theme changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    
    return () => observer.disconnect();
  }, []);

  // Extract colors for all emojis when suggestions change
  useEffect(() => {
    const colors: Record<string, string> = {};
    suggestions.forEach(suggestion => {
      if (suggestion.emoji) {
        colors[suggestion.id] = getEmojiDominantColor(suggestion.emoji);
      }
    });
    setEmojiColors(colors);
  }, [suggestions]);

  if (isLoading) {
    return (
      <div className="p-6 text-center text-gray-600 dark:text-gray-500">
        <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto mb-2"></div>
        <p className="text-sm">Loading suggestions...</p>
      </div>
    );
  }

  if (suggestions.length === 0) {
    return (
      <div className="p-6 text-center text-gray-600 dark:text-gray-500">
        <Lightbulb className="w-12 h-12 mx-auto mb-2 text-gray-400 dark:text-gray-600" />
        <p className="text-sm">No suggestions yet</p>
        <p className="text-xs text-gray-500 dark:text-gray-600 mt-1">AI will suggest next steps as you build</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4 pt-40 rounded-lg bg-gradient-to-t from-gray-50/100 via-gray-50/100 to-gray-50/0 dark:from-gray-950/100 dark:via-gray-950/100 dark:to-gray-950/0">
      {suggestions.map((suggestion, index) => {
          const gradientColor = emojiColors[suggestion.id] || 'rgb(100, 116, 139)';
          // Extract RGB values and mix with light/dark background based on theme
          const rgb = gradientColor.match(/\d+/g)?.map(Number) || [100, 116, 139];
          
          // Choose colors based on theme
          const mixedBg = isDarkMode 
            ? `rgb(${Math.round(rgb[0] * 0.08 + 31 * 0.92)}, ${Math.round(rgb[1] * 0.08 + 41 * 0.92)}, ${Math.round(rgb[2] * 0.08 + 55 * 0.92)})`
            : `rgb(${Math.round(rgb[0] * 0.08 + 255 * 0.92)}, ${Math.round(rgb[1] * 0.08 + 255 * 0.92)}, ${Math.round(rgb[2] * 0.08 + 255 * 0.92)})`;
          
          const borderColor = isDarkMode
            ? `rgb(${Math.round(rgb[0] * 0.3 + 55 * 0.7)}, ${Math.round(rgb[1] * 0.3 + 65 * 0.7)}, ${Math.round(rgb[2] * 0.3 + 75 * 0.7)})`
            : `rgb(${Math.round(rgb[0] * 0.3 + 209 * 0.7)}, ${Math.round(rgb[1] * 0.3 + 213 * 0.7)}, ${Math.round(rgb[2] * 0.3 + 219 * 0.7)})`;
          
          return (
            <motion.button
              key={suggestion.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ 
                opacity: 1, 
                y: 0,
                transition: {
                  delay: (suggestions.length - 1 - index) * 0.2,
                  duration: 0.3,
                  ease: "easeOut"
                }
              }}
              onClick={() => onSuggestionClick(suggestion.text)}
              className="relative w-full text-left px-4 py-2 border rounded-lg group flex items-center active:scale-95 hover:scale-[1.02]"
              style={{
                backgroundColor: mixedBg,
                borderColor: borderColor
              }}
            >
              <div className="flex items-center gap-3 w-full relative z-10">
                <div className="text-2xl flex-shrink-0 transition-transform duration-300">
                  {suggestion.emoji || '💡'}
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed flex-1 transition-transform duration-100 group-hover:translate-x-1">
                  {suggestion.text}
                </p>
              </div>
            </motion.button>
          );
        })}
    </div>
  );
}

